import React from 'react';
import { sanitizeString } from '../utils/security';
import { resolveRandevuBedeliGosterim } from '../utils/randevuBedeliGosterimHelper';
import { resolveOdemeDurumuGosterim } from '../utils/odemeDurumuGosterimHelper';
import AppointmentCompletionMap, { extractCompletionCoords } from './AppointmentCompletionMap';
import './AppointmentCompletionTab.css';

const safeText = (value) => sanitizeString(value ?? '');

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CompletionListPanel = ({ title, icon, items, emptyText, renderItem }) => (
  <div className="completion-panel">
    <div className="completion-panel__header">
      <span className="completion-panel__icon" aria-hidden="true">
        <i className={icon} />
      </span>
      <h3 className="completion-panel__title">{title}</h3>
      <span className="completion-panel__count">{items.length}</span>
    </div>
    {items.length > 0 ? (
      <ul className="completion-panel__list">
        {items.map((item, index) => (
          <li key={index} className="completion-panel__list-item">
            {renderItem(item, index)}
          </li>
        ))}
      </ul>
    ) : (
      <div className="completion-panel__empty">
        <i className="fas fa-inbox" aria-hidden="true" />
        <span>{emptyText}</span>
      </div>
    )}
  </div>
);

const AppointmentCompletionTab = ({ appointment }) => {
  if (!appointment) return null;

  const operations = appointment.yapilanIslemler || [];
  const parts = appointment.degistirilenParcalar || [];
  const completionCoords = extractCompletionCoords(appointment);
  const odemeDurumu = appointment.odemeDurumu;
  const odemeGosterim = resolveOdemeDurumuGosterim(appointment);
  const bedelGosterim = resolveRandevuBedeliGosterim(appointment, null);

  return (
    <div className="appointment-completion-tab">
      <div className="completion-hero">
        <div className="completion-hero__badge">
          <i className="fas fa-check-circle" aria-hidden="true" />
          Randevu Tamamlandı
        </div>
        {(appointment.odemeProblemli || odemeDurumu || appointment.odemeBeyanTahsilati) && odemeGosterim && (
          <div
            style={{
              marginTop: '12px',
              padding: '10px 14px',
              borderRadius: '8px',
              background: odemeGosterim.tone === 'success' ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${odemeGosterim.tone === 'success' ? '#86efac' : '#fecaca'}`,
              color: odemeGosterim.tone === 'success' ? '#166534' : '#991b1b',
              fontSize: '14px',
            }}
          >
            <strong>Müşteri tahsilatı:</strong> {odemeGosterim.label}
            {appointment.musteriTahsilatToplam != null && (
              <span style={{ marginLeft: '8px' }}>
                ({Number(appointment.musteriTahsilatToplam).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺)
              </span>
            )}
            {odemeGosterim.subLabel ? (
              <span style={{ display: 'block', marginTop: '6px', fontSize: '13px', color: '#64748b' }}>
                {odemeGosterim.subLabel}
              </span>
            ) : null}
            {appointment.odemeProblemli && (
              <span style={{ display: 'block', marginTop: '4px', fontSize: '13px' }}>Ödeme problemli</span>
            )}
          </div>
        )}
        {bedelGosterim.toplamTutar != null && (
          <div
            style={{
              marginTop: '8px',
              padding: '10px 14px',
              borderRadius: '8px',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              color: '#1e40af',
              fontSize: '14px',
            }}
          >
            <strong>Randevu bedeli:</strong>{' '}
            {Number(bedelGosterim.toplamTutar).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
            {bedelGosterim.altEtiket ? (
              <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#64748b' }}>
                {bedelGosterim.altEtiket}
              </span>
            ) : null}
          </div>
        )}
        {appointment.garantiSartlariSaglanmadi && (
          <div
            style={{
              marginTop: '8px',
              padding: '10px 14px',
              borderRadius: '8px',
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              color: '#92400e',
              fontSize: '13px',
            }}
          >
            Garanti şartları sağlanmadığı için işlem garanti kapsamı dışında tamamlanmıştır.
          </div>
        )}
        {appointment.servisKaydi?.odemeBeyani === true && (
          <div
            style={{
              marginTop: '8px',
              padding: '8px 12px',
              borderRadius: '8px',
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              color: '#92400e',
              fontSize: '13px',
            }}
          >
            Ödeme beyanı ile açılan servis kaydı
          </div>
        )}
        <div className="completion-hero__grid">
          <div className="completion-hero__item">
            <span className="completion-hero__label">Tamamlanma Tarihi</span>
            <span className="completion-hero__value">
              {formatDateTime(appointment.tamamlanmaTarihi || completionCoords?.tarih)}
            </span>
          </div>
          {appointment.teknisyenNotu && (
            <div className="completion-hero__item completion-hero__item--wide">
              <span className="completion-hero__label">Teknisyen Notu</span>
              <span className="completion-hero__value completion-hero__value--note">
                {safeText(appointment.teknisyenNotu)}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="completion-panels-grid">
        <CompletionListPanel
          title="Yapılan İşlemler"
          icon="fas fa-wrench"
          items={operations}
          emptyText="Kayıtlı işlem bulunmuyor"
          renderItem={(item) => (
            <>
              <span className="completion-panel__item-name">
                {safeText(item.islemAdi ?? item.icerikAdi ?? '—')}
              </span>
              {item.aciklama && (
                <span className="completion-panel__item-meta">{safeText(item.aciklama)}</span>
              )}
            </>
          )}
        />

        <CompletionListPanel
          title="Değiştirilen Parçalar"
          icon="fas fa-cogs"
          items={parts}
          emptyText="Değiştirilen parça kaydı yok"
          renderItem={(item) => (
            <>
              <span className="completion-panel__item-name">
                {safeText(item.parcaAdi ?? item.icerikAdi ?? '—')}
              </span>
              {item.adet ? (
                <span className="completion-panel__item-badge">{item.adet} adet</span>
              ) : null}
            </>
          )}
        />
      </div>

      <div className="completion-map-section">
        <div className="completion-map-section__header">
          <div className="completion-map-section__title-wrap">
            <span className="completion-map-section__icon" aria-hidden="true">
              <i className="fas fa-map-marker-alt" />
            </span>
            <div>
              <h3 className="completion-map-section__title">Tamamlanma Konumu</h3>
              <p className="completion-map-section__subtitle">
                Randevunun tamamlandığı GPS konumu
              </p>
            </div>
          </div>
          {completionCoords && (
            <div className="completion-map-section__meta">
              {completionCoords.adres && (
                <span className="completion-map-chip">
                  <i className="fas fa-location-dot" aria-hidden="true" />
                  {completionCoords.adres}
                </span>
              )}
              <span className="completion-map-chip completion-map-chip--muted">
                {completionCoords.lat.toFixed(6)}, {completionCoords.lng.toFixed(6)}
              </span>
            </div>
          )}
        </div>
        <AppointmentCompletionMap appointment={appointment} height={340} />
      </div>
    </div>
  );
};

export default AppointmentCompletionTab;
